import { updateKnowledgeBaseAccordionError, updateKnowledgeItemErrors } from "../../redux/actions/KnowledgeBaseActions";
import store from "../../redux/store";
import { compareObjects, validateField } from "../base-logic";

export const KNOWLEDGE_BASE_KEY = "knowledge-base";

export const OPENING_HOURS_KEY = "opening-hours";
export const TEXT_KNOWLEDGE_ITEM_KEY = "plain-text";

export const invalidKnowledgeBaseKeys = ["invalid-opening-hours", "invalid-plain-text", "min-knowledge-base-items"];

export const hasChangesInKnowledgeBase = () => {
  const knowledgeBase = store.getState().knowledgeBase;
  if (!knowledgeBase) {
    return false;
  }

  return !compareObjects(knowledgeBase.knowledgeBaseItems, knowledgeBase.updatedKnowledgeBaseItems);
};

export const getKnowledgeItemErrors = (knowledgeItem) => {
  const config = store.getState().knowledgeBase.config;
  const errors = {};

  if (knowledgeItem.type === OPENING_HOURS_KEY) {
    errors.value = validateField(
      true,
      "Opening hours",
      knowledgeItem.value,
      0,
      "",
      validateOpeningHours,
      "Invalid opening hours"
    );
  }

  if (knowledgeItem.type === TEXT_KNOWLEDGE_ITEM_KEY) {
    errors.title = validateField(true, "Title", knowledgeItem.title, config.minKnowledgeItemTitleCharacters);
    errors.value = validateField(
      true,
      "Information",
      knowledgeItem.value,
      config.minKnowledgeItemDescriptionCharacters
    );
  }

  return errors;
};

export const getKnowledgeItemFieldError = (knowledgeItem, key, value) => {
  const config = store.getState().knowledgeBase.config;

  switch (knowledgeItem.type) {
    case OPENING_HOURS_KEY:
      return value.length === 0 ? "Opening hours are required" : "";
    case TEXT_KNOWLEDGE_ITEM_KEY:
      if (key === "title") {
        return validateField(true, "Title", knowledgeItem.title, config.minKnowledgeItemTitleCharacters);
      }

      if (key === "value") {
        return validateField(true, "Information", knowledgeItem.value, config.minKnowledgeItemDescriptionCharacters);
      }
    default:
      return "";
  }
};

export const validateKnowledgeBase = () => {
  const knowledgeBase = store.getState().knowledgeBase;
  if (!knowledgeBase) {
    return false;
  }

  if (knowledgeBase.updatedKnowledgeBaseItems.length === 0 && knowledgeBase.config.minKnowledgeItemsListSize === 0) {
    return { valid: false, type: "no-knowledge-base-items" };
  }

  if (
    knowledgeBase.config.minKnowledgeItemsListSize > 0 &&
    knowledgeBase.updatedKnowledgeBaseItems.length < knowledgeBase.config.minKnowledgeItemsListSize
  ) {
    const errorMessage =
      "You need to add at least " +
      (knowledgeBase.config.minKnowledgeItemsListSize === 1
        ? "1 item"
        : knowledgeBase.config.minKnowledgeItemsListSize + " items");

    store.dispatch(updateKnowledgeBaseAccordionError(errorMessage));
    return { valid: false, type: "min-knowledge-base-items", elementId: `knowledge-base-accordion` };
  }

  for (var i = 0; i < knowledgeBase.updatedKnowledgeBaseItems.length; i++) {
    const knowledgeBaseItem = knowledgeBase.updatedKnowledgeBaseItems[i];
    var validate = validateKnowledgeBaseItem(knowledgeBaseItem, i);
    if (!validate.valid) {
      // update errors item
      const knowledgeItemErrors = [...knowledgeBase.knowledgeItemErrors];
      knowledgeItemErrors[i] = getKnowledgeItemErrors(knowledgeBaseItem);
      store.dispatch(updateKnowledgeItemErrors(knowledgeItemErrors));
      return validate;
    }
  }

  return { valid: true };
};

export const validateKnowledgeBaseItem = (item, index) => {
  if (item.type === OPENING_HOURS_KEY) {
    const isValid = validateOpeningHours(item.value);
    if (!isValid) {
      return {
        valid: false,
        type: "invalid-opening-hours",
        message: "Required fields missing.",
        elementId: `knowledge-item-${index}`,
        accordionIndex: index,
        errors: { value: "Opening hours are required" },
      };
    }
  }

  if (item.type === TEXT_KNOWLEDGE_ITEM_KEY) {
    const isValid = validateTextKnowledgeItem(item);
    if (!isValid) {
      return {
        valid: false,
        type: "invalid-plain-text",
        message: "Required fields missing.",
        elementId: `knowledge-item-${index}`,
        accordionIndex: index,
        errors: { title: item.title ? "" : "Title is required", value: item.value ? "" : "Information is required" },
      };
    }
  }

  return { valid: true };
};

export const validateOpeningHours = (openingHours) => {
  if (openingHours.length === 0) {
    return false;
  }

  for (var i = 0; i < openingHours.length; i++) {
    if (openingHours[i].day === "" || !openingHours[i].open || !openingHours[i].close) {
      return false;
    }
  }

  // const isValid = validateDuplicateOpeningHours(openingHours);
  // if (!isValid) {
  //   return false;
  // }

  return true;
};

// Function to check for duplicate or overlapping time slots and return their indices
const validateDuplicateOpeningHours = (openingHours) => {
  // Group time intervals by day
  const groupedByDay = openingHours.reduce((acc, curr) => {
    if (!acc[curr.day]) {
      acc[curr.day] = [];
    }
    acc[curr.day].push({ open: curr.open, close: curr.close });
    return acc;
  }, {});

  // Function to check if two intervals overlap
  const isOverlap = (interval1, interval2) => interval1.open < interval2.close && interval2.open < interval1.close;

  // Check for overlaps within each day
  for (const day in groupedByDay) {
    const intervals = groupedByDay[day];
    for (let i = 0; i < intervals.length; i++) {
      for (let j = i + 1; j < intervals.length; j++) {
        if (isOverlap(intervals[i], intervals[j])) {
          return false; // Overlap found
        }
      }
    }
  }

  return true; // No overlaps found
};

export const validateTextKnowledgeItem = (item, index) => {
  const config = store.getState().knowledgeBase.config;

  const title = item.title;
  if (title.length < config.minKnowledgeItemTitleCharacters) {
    return false;
  }

  const information = item.value;
  if (information && information.length < config.minKnowledgeItemDescriptionCharacters) {
    return false;
  }

  return true;
};

export const getKnowledgeBaseSaveObject = () => {
  const knowledgeBase = store.getState().knowledgeBase;
  if (!knowledgeBase) {
    return null;
  }

  const items = knowledgeBase.updatedKnowledgeBaseItems.map((item) => {
    if (item.type === OPENING_HOURS_KEY) {
      return {
        type: item.type,
        title: item.title,
        openingHours: item.value,
      };
    }
    return {
      type: item.type,
      title: item.title.trim(),
      content: item.value.trim(),
    };
  });

  return { items };
};

export const isEmptyKnowledgeItem = (item) => {
  if (item.type === OPENING_HOURS_KEY) {
    return item.value.length === 0;
  }

  if (item.type === TEXT_KNOWLEDGE_ITEM_KEY) {
    return item.title === "" && item.value === "";
  }

  return false;
};

export const sortKnowledgeBaseItems = (items) => {
  if (!items) return [];
  return [...items].sort((a, b) => a.priority - b.priority);
};
